import * as THREE from "three";
import { fitOrthoZoom, fitPerspectiveDistance } from "./cameraFit";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { UnrealBloomPass } from "three/examples/jsm/postprocessing/UnrealBloomPass.js";
import { OutputPass } from "three/examples/jsm/postprocessing/OutputPass.js";
import type { Pass } from "three/examples/jsm/postprocessing/Pass.js";
import {
  BLOOM_RADIUS,
  BLOOM_STRENGTH,
  BLOOM_THRESHOLD,
  CAMERA_3D_PHI,
  CAMERA_3D_THETA,
  CAMERA_FAR,
  CAMERA_FIT_MARGIN,
  CAMERA_MAX_DISTANCE,
  CAMERA_MIN_DISTANCE,
  CAMERA_TARGET_BOUND,
  ORTHO_MIN_ZOOM,
  PLACE_BOUND_MASK_COLOR,
  PLACE_BOUND_MASK_OPACITY,
  PLACE_BOUND_XY,
  GRID_COLOR,
  GRID_DIVISIONS,
  GRID_OPACITY,
  GRID_SIZE,
  CAMERA_FOV_2D,
  CAMERA_FOV_3D,
  CAMERA_NEAR,
  ENV_INTENSITY,
  KEY_LIGHT_DIR,
  KEY_LIGHT_DIR_Z,
  KEY_LIGHT_DISTANCE,
  KEY_LIGHT_INTENSITY,
  SHADOW_MAP_SIZE,
  TONE_MAPPING_EXPOSURE,
  VIEW_HEIGHT,
  VIEW_TWEEN_S,
} from "./constants";

export type ViewMode = "2d" | "3d";

/**
 * A serialisable snapshot of both cameras' poses plus the active mode. Both
 * cameras are always live in the rig, so capturing both lets a restore reproduce
 * the saved view regardless of which mode the rig happens to be in at load time.
 */
export interface CameraPose {
  mode: ViewMode;
  persp: { position: [number, number, number]; target: [number, number, number] };
  ortho: { position: [number, number, number]; target: [number, number, number]; zoom: number };
}

export type BloomMode = "off" | "quarter" | "half";

export interface DebugRenderState {
  dprCap1: boolean;
  bloom: BloomMode;
  postFx: boolean;
}

export interface PhaseTimer {
  begin: (name: string) => void;
  end: (name: string) => void;
}

export const RENDER_PHASE_SCENE = "scene";
export const RENDER_PHASE_BLOOM = "bloom";
export const RENDER_PHASE_POST = "post";

export interface SceneBundle {
  renderer: THREE.WebGLRenderer;
  scene: THREE.Scene;
  rig: CameraRig;
  grid: THREE.GridHelper;
  resize: (width: number, height: number) => void;
  render: (camera: THREE.Camera) => void;
  setFeedActive: (active: boolean) => void;
  setFeedRect: (rect: { x: number; y: number; w: number; h: number } | null) => void;
  setDebugRender: (state: DebugRenderState) => void;
  setDprCap1: (cap: boolean) => void;
  setBloomMode: (mode: BloomMode) => void;
  setPhaseTimer: (timer: PhaseTimer | null) => void;
  setShadowMapSize: (size: number) => void;
  setKeyLightDir: (x: number, y: number) => void;
}

function distForFov(fovDeg: number): number {
  return VIEW_HEIGHT / (2 * Math.tan((fovDeg * Math.PI) / 360));
}

const INITIAL_ORTHO_ZOOM = 1;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function easeInOut(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

/**
 * Two-camera rig. The PerspectiveCamera drives 3D and every 2D⇄3D tween (narrow
 * FOV + far dolly approximating ortho at the 2D endpoint, wide FOV + near dolly
 * at 3D); its dolly distance always makes the z=0 focal plane span exactly
 * VIEW_HEIGHT. The OrthographicCamera sits at that same dolly distance with a
 * VIEW_HEIGHT-tall frustum, so swapping to it at the static 2D endpoint is
 * pixel-equivalent and gives true parallel projection (3D meshes show no
 * perspective convergence). `activeCamera` returns ortho only at the static 2D
 * endpoint and perspective otherwise, so tweens stay on the perspective path.
 * OrbitControls is enabled only in full 3D.
 */
export class CameraRig {
  readonly camera: THREE.PerspectiveCamera;
  readonly orthoCamera: THREE.OrthographicCamera;
  readonly controls: OrbitControls;
  readonly orthoControls: OrbitControls;
  private mode: ViewMode;
  private targetMode: ViewMode;
  private tweening: boolean;
  private tweenElapsed: number;
  private fromSph: THREE.Spherical;
  private toSph: THREE.Spherical;
  private fromFov: number;
  private toFov: number;
  private fromZLock: number;
  private toZLock: number;
  private zLockValue: number;
  private focalDistValue: number;
  private cameraEnabled: boolean;

  constructor(
    camera: THREE.PerspectiveCamera,
    orthoCamera: THREE.OrthographicCamera,
    controls: OrbitControls,
    orthoControls: OrbitControls,
  ) {
    this.camera = camera;
    this.orthoCamera = orthoCamera;
    this.controls = controls;
    this.orthoControls = orthoControls;
    this.mode = "2d";
    this.targetMode = "2d";
    this.tweening = false;
    this.tweenElapsed = 0;
    this.fromSph = new THREE.Spherical();
    this.toSph = new THREE.Spherical();
    this.fromFov = CAMERA_FOV_2D;
    this.toFov = CAMERA_FOV_2D;
    this.fromZLock = 1;
    this.toZLock = 1;
    this.zLockValue = 1;
    this.focalDistValue = distForFov(CAMERA_FOV_2D);
    this.cameraEnabled = true;
    this.orthoCamera.position.set(0, 0, distForFov(CAMERA_FOV_2D));
    this.orthoCamera.lookAt(0, 0, 0);
    this.orthoCamera.updateProjectionMatrix();
    this.jumpTo("2d");
  }

  get activeCamera(): THREE.Camera {
    return this.mode === "2d" && !this.tweening ? this.orthoCamera : this.camera;
  }

  updateOrthoFrustum(aspect: number): void {
    const halfH = VIEW_HEIGHT / 2;
    const halfW = halfH * aspect;
    this.orthoCamera.left = -halfW;
    this.orthoCamera.right = halfW;
    this.orthoCamera.top = halfH;
    this.orthoCamera.bottom = -halfH;
    this.orthoCamera.updateProjectionMatrix();
  }

  jumpTo(mode: ViewMode): void {
    this.mode = mode;
    this.targetMode = mode;
    this.tweening = false;
    this.zLockValue = mode === "3d" ? 0 : 1;
    this.camera.fov = mode === "3d" ? CAMERA_FOV_3D : CAMERA_FOV_2D;
    const dist = distForFov(this.camera.fov);
    if (mode === "3d") {
      this.camera.position.setFromSpherical(new THREE.Spherical(dist, CAMERA_3D_PHI, CAMERA_3D_THETA));
      this.controls.target.set(0, 0, 0);
    } else {
      this.camera.position.set(0, 0, dist);
      this.controls.target.set(0, 0, 0);
      this.resetOrtho();
    }
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
    this.syncControlState();
    this.activeControls().update();
    this.focalDistValue = dist;
  }

  /**
   * Recentre the ortho camera + its OrbitControls to the origin home pose. Mode
   * switches reset 2D pan/zoom so swapping dimensions never strands the view
   * off-centre while the perspective tween returns to its own fixed pose.
   */
  private resetOrtho(): void {
    const dist = distForFov(CAMERA_FOV_2D);
    this.orthoCamera.position.set(0, 0, dist);
    this.orthoCamera.zoom = 1;
    this.orthoControls.target.set(0, 0, 0);
    this.orthoCamera.lookAt(0, 0, 0);
    this.orthoCamera.updateProjectionMatrix();
  }

  private activeControls(): OrbitControls {
    return this.mode === "2d" ? this.orthoControls : this.controls;
  }

  /**
   * 2D drives the ortho OrbitControls (pan + zoom, no rotate), 3D drives the
   * perspective controls (orbit + pan + zoom); during a tween both are off so
   * pointer input never fights the camera animation. The Camera toggle
   * (cameraEnabled false) keeps both off in either mode.
   */
  private syncControlState(): void {
    if (this.tweening || !this.cameraEnabled) {
      this.controls.enabled = false;
      this.orthoControls.enabled = false;
    } else if (this.mode === "3d") {
      this.controls.enabled = true;
      this.orthoControls.enabled = false;
    } else {
      this.controls.enabled = false;
      this.orthoControls.enabled = true;
    }
  }

  setMode(mode: ViewMode): void {
    if (this.tweening ? this.targetMode === mode : this.mode === mode) {
      return;
    }
    this.targetMode = mode;
    this.controls.enabled = false;
    this.orthoControls.enabled = false;
    this.fromSph.setFromVector3(this.camera.position);
    this.fromFov = this.camera.fov;
    this.fromZLock = this.zLockValue;
    this.toFov = mode === "3d" ? CAMERA_FOV_3D : CAMERA_FOV_2D;
    this.toZLock = mode === "3d" ? 0 : 1;
    const toDist = distForFov(this.toFov);
    if (mode === "3d") {
      this.toSph.set(toDist, CAMERA_3D_PHI, CAMERA_3D_THETA);
    } else {
      this.toSph.set(toDist, Math.PI / 2, 0);
    }
    this.tweenElapsed = 0;
    this.tweening = true;
  }

  update(dt: number): void {
    if (this.tweening) {
      this.tweenElapsed += dt;
      const t = Math.min(this.tweenElapsed / VIEW_TWEEN_S, 1);
      const e = easeInOut(t);
      const cur = new THREE.Spherical(
        lerp(this.fromSph.radius, this.toSph.radius, e),
        lerp(this.fromSph.phi, this.toSph.phi, e),
        lerp(this.fromSph.theta, this.toSph.theta, e),
      );
      cur.makeSafe();
      this.camera.position.setFromSpherical(cur);
      this.camera.lookAt(0, 0, 0);
      this.camera.fov = lerp(this.fromFov, this.toFov, e);
      this.camera.updateProjectionMatrix();
      this.zLockValue = lerp(this.fromZLock, this.toZLock, e);
      if (t >= 1) {
        this.tweening = false;
        this.mode = this.targetMode;
        if (this.mode === "3d") {
          this.controls.target.set(0, 0, 0);
        } else {
          this.resetOrtho();
        }
        this.syncControlState();
        this.activeControls().update();
      }
    } else {
      this.activeControls().update();
      this.clampActiveTarget();
    }
    this.focalDistValue = this.camera.position.distanceTo(this.controls.target);
  }

  /**
   * Fences the active orbit target inside the diorama pan box so pan can't strand
   * the view off the playable area. Pan shifts target and camera together, so the
   * same correction delta is applied to the camera position to preserve the view
   * direction and distance — only the target is bounded, not the orbit pose. Runs
   * only in the steady state; tweens and programmatic jumps/fits land inside the
   * box by construction, so they are never clamped.
   */
  private clampActiveTarget(): void {
    const controls = this.activeControls();
    const cam = this.mode === "2d" ? this.orthoCamera : this.camera;
    const t = controls.target;
    const cx = THREE.MathUtils.clamp(t.x, -CAMERA_TARGET_BOUND, CAMERA_TARGET_BOUND);
    const cy = THREE.MathUtils.clamp(t.y, -CAMERA_TARGET_BOUND, CAMERA_TARGET_BOUND);
    const cz = THREE.MathUtils.clamp(t.z, -CAMERA_TARGET_BOUND, CAMERA_TARGET_BOUND);
    const dx = cx - t.x;
    const dy = cy - t.y;
    const dz = cz - t.z;
    if (dx === 0 && dy === 0 && dz === 0) {
      return;
    }
    t.set(cx, cy, cz);
    cam.position.set(cam.position.x + dx, cam.position.y + dy, cam.position.z + dz);
  }

  zLock(): number {
    return this.zLockValue;
  }

  focalDist(): number {
    return this.focalDistValue;
  }

  canPlace(): boolean {
    return this.mode === "2d" && !this.tweening;
  }

  isSteady(): boolean {
    return !this.tweening;
  }

  setCameraEnabled(on: boolean): void {
    this.cameraEnabled = on;
    this.syncControlState();
  }

  /**
   * Transiently silences orbit/pan so a canvas drag (= marquee endpoint) owns
   * the gesture instead of fighting the camera. resumeControls restores the
   * cameraEnabled/mode-derived state.
   */
  suspendControls(): void {
    this.controls.enabled = false;
    this.orthoControls.enabled = false;
  }

  resumeControls(): void {
    this.syncControlState();
  }

  /** Snapshots both cameras' live poses and the steady mode (the tween's target while animating). */
  captureCamera(): CameraPose {
    return {
      mode: this.tweening ? this.targetMode : this.mode,
      persp: {
        position: [this.camera.position.x, this.camera.position.y, this.camera.position.z],
        target: [this.controls.target.x, this.controls.target.y, this.controls.target.z],
      },
      ortho: {
        position: [this.orthoCamera.position.x, this.orthoCamera.position.y, this.orthoCamera.position.z],
        target: [this.orthoControls.target.x, this.orthoControls.target.y, this.orthoControls.target.z],
        zoom: this.orthoCamera.zoom,
      },
    };
  }

  /**
   * Jumps both cameras to a captured pose and lands in the pose's mode with no
   * tween (a scene load reproduces the view instantly). Re-establishes the same
   * post-conditions as jumpTo: per-mode fov, zLock, control enable-state, focal
   * distance, and a steady (non-tweening) rig.
   */
  restoreCamera(pose: CameraPose): void {
    this.camera.position.set(pose.persp.position[0], pose.persp.position[1], pose.persp.position[2]);
    this.controls.target.set(pose.persp.target[0], pose.persp.target[1], pose.persp.target[2]);
    this.orthoCamera.position.set(pose.ortho.position[0], pose.ortho.position[1], pose.ortho.position[2]);
    this.orthoControls.target.set(pose.ortho.target[0], pose.ortho.target[1], pose.ortho.target[2]);
    this.orthoCamera.zoom = pose.ortho.zoom;
    this.mode = pose.mode;
    this.targetMode = pose.mode;
    this.tweening = false;
    this.zLockValue = pose.mode === "3d" ? 0 : 1;
    this.camera.fov = pose.mode === "3d" ? CAMERA_FOV_3D : CAMERA_FOV_2D;
    this.camera.lookAt(this.controls.target);
    this.camera.updateProjectionMatrix();
    this.orthoCamera.lookAt(this.orthoControls.target);
    this.orthoCamera.updateProjectionMatrix();
    this.focalDistValue = this.camera.position.distanceTo(this.controls.target);
    this.syncControlState();
    this.activeControls().update();
  }

  /**
   * Reframes the active camera so `box` fits the viewport at `aspect`, keeping
   * the current view angle: 3D moves only the dolly distance + target along the
   * existing view direction; 2D moves only the ortho zoom + centre. The box's
   * bounding sphere governs the 3D distance (angle-independent, so any orbit
   * pose fits), while 2D fits the box's x/y half-extents directly. No-op on an
   * empty box or while tweening (the angle would be mid-flight).
   */
  fitToBox(box: THREE.Box3, aspect: number): void {
    if (box.isEmpty() || this.tweening) {
      return;
    }
    const center = box.getCenter(new THREE.Vector3());
    if (this.mode === "3d") {
      const sphere = box.getBoundingSphere(new THREE.Sphere());
      const fitDist = fitPerspectiveDistance(sphere.radius, this.camera.fov, aspect, CAMERA_FIT_MARGIN);
      const dist = Math.min(Math.max(fitDist, distForFov(CAMERA_FOV_3D)), CAMERA_MAX_DISTANCE);
      const dir = new THREE.Vector3().subVectors(this.camera.position, this.controls.target).normalize();
      this.controls.target.copy(center);
      this.camera.position.copy(center).addScaledVector(dir, dist);
      this.camera.lookAt(center);
      this.camera.updateProjectionMatrix();
      this.controls.update();
      this.focalDistValue = dist;
    } else {
      const size = box.getSize(new THREE.Vector3());
      const fitZoom = fitOrthoZoom(size.x / 2, size.y / 2, VIEW_HEIGHT / 2, aspect, CAMERA_FIT_MARGIN);
      this.orthoCamera.zoom = Math.max(Math.min(fitZoom, INITIAL_ORTHO_ZOOM), ORTHO_MIN_ZOOM);
      this.orthoCamera.position.set(center.x, center.y, this.orthoCamera.position.z);
      this.orthoControls.target.set(center.x, center.y, 0);
      this.orthoCamera.lookAt(center.x, center.y, 0);
      this.orthoCamera.updateProjectionMatrix();
      this.orthoControls.update();
    }
  }
}

const SKY_VERTEX = `
varying vec3 vWorldPosition;
void main() {
  vWorldPosition = (modelMatrix * vec4(position, 1.0)).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const SKY_FRAGMENT = `
varying vec3 vWorldPosition;
uniform vec3 uTop;
uniform vec3 uBottom;
void main() {
  float t = clamp(normalize(vWorldPosition - cameraPosition).y * 0.5 + 0.5, 0.0, 1.0);
  gl_FragColor = vec4(mix(uBottom, uTop, t), 1.0);
}
`;

const SKYDOME_RADIUS = 300;

/**
 * The gradient runs over the normalized view direction's y, so it is
 * scale-invariant: with the dome recentered on the camera every frame
 * (see createScene's render), direction.y equals the old vertex-y / radius
 * mapping (t = y/600 + 0.5 seen from the center) and the dome cannot be
 * exited no matter how far the camera moves.
 */
function createSkydome(): THREE.Mesh {
  const geometry = new THREE.SphereGeometry(SKYDOME_RADIUS, 32, 16);
  const material = new THREE.ShaderMaterial({
    uniforms: {
      uTop: { value: new THREE.Color(0x40518c) },
      uBottom: { value: new THREE.Color(0x42424c) },
    },
    vertexShader: SKY_VERTEX,
    fragmentShader: SKY_FRAGMENT,
    side: THREE.BackSide,
    depthWrite: false,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.renderOrder = -1;
  return mesh;
}

const BOUNDS_MASK_WALL_OFFSET = 0.1;

/**
 * A world-fixed gray frame that tints the wallpaper outside the placement bounds,
 * so the playable diorama reads as a lit window in a dimmed surround. The shape is
 * a large quad with a ±PLACE_BOUND_XY hole; it rides just in front of the wall (z
 * tracks the wall, see BackgroundWall.setZ) but behind the z=0 play plane, so
 * depth-test keeps particles/objects untinted while the backdrop greys out. Static
 * in world XY (the wall scrolls under it), so panning reveals the same fixed frame.
 */
function createBoundsMask(): THREE.Mesh {
  const outer = CAMERA_FAR;
  const shape = new THREE.Shape();
  shape.moveTo(-outer, -outer);
  shape.lineTo(outer, -outer);
  shape.lineTo(outer, outer);
  shape.lineTo(-outer, outer);
  shape.closePath();
  const hole = new THREE.Path();
  hole.moveTo(-PLACE_BOUND_XY, -PLACE_BOUND_XY);
  hole.lineTo(-PLACE_BOUND_XY, PLACE_BOUND_XY);
  hole.lineTo(PLACE_BOUND_XY, PLACE_BOUND_XY);
  hole.lineTo(PLACE_BOUND_XY, -PLACE_BOUND_XY);
  hole.closePath();
  shape.holes.push(hole);
  const material = new THREE.MeshBasicMaterial({
    color: PLACE_BOUND_MASK_COLOR,
    transparent: true,
    opacity: PLACE_BOUND_MASK_OPACITY,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(new THREE.ShapeGeometry(shape), material);
  mesh.frustumCulled = false;
  return mesh;
}

export type {
  WallPalette,
  WallParams,
  WallPattern,
} from "./wallMaps";
export {
  WALL_DEFAULT_NORMAL_SCALE,
  WALL_DEFAULT_PARAMS,
  WALL_DEFAULT_PRESET,
  WALL_DEFAULT_RESOLUTION,
  WALL_DEFAULT_TILE,
  WALL_DEFAULT_Z,
  WALL_PRESETS,
  WALL_RESOLUTIONS,
  wallParamsToPreset,
  wallPresetNormalScale,
  wallPresetParams,
} from "./wallMaps";
export type { WallPreset } from "./wallMaps";

import {
  wrapWallMaps,
  WALL_DEFAULT_NORMAL_SCALE,
  WALL_DEFAULT_PARAMS,
  WALL_DEFAULT_TILE,
  WALL_DEFAULT_Z,
  WALL_PALETTES,
  WALL_PRESETS,
  WALL_TEX_H,
  WALL_TEX_W,
  wallPresetParams,
  wallWorldRepeat,
  type WallMaps,
  type WallParams,
} from "./wallMaps";
import WallBakeWorker from "./wallBakeWorker.ts?worker";
import type { WallBakeRequest, WallBakeResponse } from "./wallBakeWorker";

const WALL_WORLD_UV_VERTEX = /* glsl */ `
uniform float uWorldRepeatX;
uniform float uWorldRepeatY;
`;

const WALL_WORLD_UV_OVERRIDE = /* glsl */ `
{
  vec4 wallWorldPos = modelMatrix * vec4(position, 1.0);
  vec2 wallUv = wallWorldPos.xy / vec2(uWorldRepeatX, uWorldRepeatY);
  #ifdef USE_MAP
    vMapUv = wallUv;
  #endif
  #ifdef USE_NORMALMAP
    vNormalMapUv = wallUv;
  #endif
}
`;

function attachWallWorldUvMaterial(material: THREE.MeshStandardMaterial): void {
  material.onBeforeCompile = (shader: THREE.WebGLProgramParametersWithUniforms): void => {
    shader.uniforms.uWorldRepeatX = material.userData.uWorldRepeatX as { value: number };
    shader.uniforms.uWorldRepeatY = material.userData.uWorldRepeatY as { value: number };
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>\n${WALL_WORLD_UV_VERTEX}`)
      .replace("#include <uv_vertex>", `#include <uv_vertex>\n${WALL_WORLD_UV_OVERRIDE}`);
  };
  material.customProgramCacheKey = (): string => "background-wall-tex";
}

export interface BackgroundWall {
  mesh: THREE.Mesh;
  boundsMask: THREE.Mesh;
  setTiling: (tiles: number) => void;
  setNormalScale: (scale: number) => void;
  setResolution: (w: number, h: number) => void;
  setZ: (z: number) => void;
  followCamera: (camera: THREE.Camera) => void;
  regenerate: (params: WallParams) => void;
  prebakeAll: () => void;
  getParams: () => WallParams;
}

/**
 * The quad spans 2×CAMERA_FAR and followCamera copies the camera x/y every
 * frame (z stays at the wall-z slider value), so the wall reads as an
 * infinite plane. Textured modes bake noise/voronoi maps in a Web Worker and
 * sample them with world-space UVs so the pattern stays fixed while the quad
 * moves. Baked maps are cached by preset + resolution and never disposed, so
 * re-selecting a preset is instant and never blocks the main thread.
 */
export function createBackgroundWall(): BackgroundWall {
  const size = CAMERA_FAR * 2;
  let tiles = WALL_DEFAULT_TILE;
  let texW = WALL_TEX_W;
  let texH = WALL_TEX_H;
  let currentParams: WallParams = { ...WALL_DEFAULT_PARAMS };
  const worldRepeat = wallWorldRepeat(tiles);
  const texturedMaterial = new THREE.MeshStandardMaterial({
    color: WALL_PALETTES[WALL_DEFAULT_PARAMS.palette].base,
    normalScale: new THREE.Vector2(WALL_DEFAULT_NORMAL_SCALE, WALL_DEFAULT_NORMAL_SCALE),
    metalness: 0,
    roughness: 0.85,
  });
  texturedMaterial.userData.uWorldRepeatX = { value: worldRepeat.x };
  texturedMaterial.userData.uWorldRepeatY = { value: worldRepeat.y };
  attachWallWorldUvMaterial(texturedMaterial);
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(size, size), texturedMaterial);
  mesh.position.z = WALL_DEFAULT_Z;
  mesh.receiveShadow = true;
  mesh.frustumCulled = false;
  const boundsMask = createBoundsMask();
  boundsMask.position.z = WALL_DEFAULT_Z + BOUNDS_MASK_WALL_OFFSET;
  const cameraPos = new THREE.Vector3();

  const worker = new WallBakeWorker();
  let bakeSeq = 0;
  const pending = new Map<number, (r: WallBakeResponse) => void>();
  worker.onmessage = (e: MessageEvent<WallBakeResponse>): void => {
    const cb = pending.get(e.data.id);
    if (cb !== undefined) {
      pending.delete(e.data.id);
      cb(e.data);
    }
  };
  const requestBake = (params: WallParams, w: number, h: number, cb: (r: WallBakeResponse) => void): void => {
    const req: WallBakeRequest = { id: ++bakeSeq, params, sizeX: w, sizeY: h };
    pending.set(req.id, cb);
    worker.postMessage(req);
  };

  const cache = new Map<string, WallMaps>();
  const inflight = new Map<string, ((m: WallMaps) => void)[]>();
  const cacheKey = (p: WallParams, w: number, h: number): string =>
    `${p.pattern}-${p.palette}-${p.cells}-${p.octaves}-${p.accentRatio}-${p.edgeWidth}-${p.edgeDepth}-${p.detail}-${w}x${h}`;
  let generation = 0;

  const applyWorldRepeat = (): void => {
    const repeat = wallWorldRepeat(tiles);
    (texturedMaterial.userData.uWorldRepeatX as { value: number }).value = repeat.x;
    (texturedMaterial.userData.uWorldRepeatY as { value: number }).value = repeat.y;
  };
  const applyMaps = (m: WallMaps, palette: WallParams["palette"]): void => {
    texturedMaterial.map = m.colorMap;
    texturedMaterial.normalMap = m.normalMap;
    texturedMaterial.color.set(m.colorMap !== null ? 0xffffff : WALL_PALETTES[palette].base);
    texturedMaterial.needsUpdate = true;
    applyWorldRepeat();
  };
  /**
   * Resolves the cached maps for params at the current resolution, baking in
   * the worker on a miss. Concurrent requests for the same key share one bake
   * (the callbacks queue in inflight) so prebake + a focus swap never double
   * the work.
   */
  const requestMaps = (params: WallParams, key: string, cb: (m: WallMaps) => void): void => {
    const cached = cache.get(key);
    if (cached !== undefined) {
      cb(cached);
      return;
    }
    const waiting = inflight.get(key);
    if (waiting !== undefined) {
      waiting.push(cb);
      return;
    }
    inflight.set(key, [cb]);
    requestBake(params, texW, texH, (r: WallBakeResponse): void => {
      const maps = wrapWallMaps({ normal: r.normal, color: r.color, sizeX: r.sizeX, sizeY: r.sizeY });
      cache.set(key, maps);
      const cbs = inflight.get(key) ?? [];
      inflight.delete(key);
      for (const f of cbs) {
        f(maps);
      }
    });
  };
  const bakeAndApply = (params: WallParams): void => {
    generation++;
    const myGen = generation;
    requestMaps(params, cacheKey(params, texW, texH), (maps: WallMaps): void => {
      if (myGen === generation) {
        applyMaps(maps, params.palette);
      }
    });
  };

  return {
    mesh,
    boundsMask,
    setTiling: (t: number): void => {
      tiles = t;
      applyWorldRepeat();
    },
    setNormalScale: (scale: number): void => {
      texturedMaterial.normalScale.set(scale, scale);
    },
    setResolution: (w: number, h: number): void => {
      texW = w;
      texH = h;
      if (currentParams.pattern !== "none") {
        bakeAndApply(currentParams);
      }
    },
    setZ: (z: number): void => {
      mesh.position.z = z;
      boundsMask.position.z = z + BOUNDS_MASK_WALL_OFFSET;
    },
    followCamera: (camera: THREE.Camera): void => {
      camera.getWorldPosition(cameraPos);
      mesh.position.x = cameraPos.x;
      mesh.position.y = cameraPos.y;
    },
    regenerate: (params: WallParams): void => {
      currentParams = { ...params };
      mesh.visible = params.pattern !== "none";
      boundsMask.visible = params.pattern !== "none";
      if (params.pattern === "none") {
        generation++;
        texturedMaterial.map = null;
        texturedMaterial.normalMap = null;
        texturedMaterial.color.set(WALL_PALETTES[params.palette].base);
        texturedMaterial.needsUpdate = true;
        return;
      }
      bakeAndApply(params);
    },
    prebakeAll: (): void => {
      for (const preset of WALL_PRESETS) {
        if (preset === "none") {
          continue;
        }
        const params = wallPresetParams(preset);
        requestMaps(params, cacheKey(params, texW, texH), (): void => {});
      }
    },
    getParams: (): WallParams => ({ ...currentParams }),
  };
}

function setupEnvironment(renderer: THREE.WebGLRenderer, scene: THREE.Scene): void {
  const pmrem = new THREE.PMREMGenerator(renderer);
  scene.environment = pmrem.fromScene(new RoomEnvironment()).texture;
  scene.environmentIntensity = ENV_INTENSITY;
  pmrem.dispose();
}

interface LightingControls {
  setShadowMapSize: (size: number) => void;
  setKeyLightDir: (x: number, y: number) => void;
}

/**
 * setShadowMapSize reallocates the shadow map at a new resolution (mapSize
 * changes only take effect after the existing render target is disposed).
 * setKeyLightDir repositions the key light from raw x/y components with the
 * z component fixed at KEY_LIGHT_DIR_Z.
 */
function setupLighting(scene: THREE.Scene): LightingControls {
  const key = new THREE.DirectionalLight(0xffffff, KEY_LIGHT_INTENSITY);
  key.position.copy(KEY_LIGHT_DIR).multiplyScalar(KEY_LIGHT_DISTANCE);
  key.target.position.set(0, 0, 0);
  key.castShadow = true;
  key.shadow.camera.left = -25;
  key.shadow.camera.right = 25;
  key.shadow.camera.top = 25;
  key.shadow.camera.bottom = -25;
  key.shadow.camera.near = KEY_LIGHT_DISTANCE - 30;
  key.shadow.camera.far = KEY_LIGHT_DISTANCE + 40;
  key.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE);
  key.shadow.normalBias = 0.05;
  key.shadow.camera.updateProjectionMatrix();
  scene.add(key);
  scene.add(key.target);
  return {
    setShadowMapSize: (size: number): void => {
      key.shadow.mapSize.set(size, size);
      if (key.shadow.map) {
        key.shadow.map.dispose();
        key.shadow.map = null;
      }
    },
    setKeyLightDir: (x: number, y: number): void => {
      key.position.set(x, y, KEY_LIGHT_DIR_Z).normalize().multiplyScalar(KEY_LIGHT_DISTANCE);
    },
  };
}

const EDITOR_CLEAR_COLOR = 0x0a0a0f;

export function createScene(canvas: HTMLCanvasElement, initialBloomMode: BloomMode = "half"): SceneBundle {
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setClearColor(EDITOR_CLEAR_COLOR, 1);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.localClippingEnabled = true;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFShadowMap;

  const scene = new THREE.Scene();
  const skydome = createSkydome();
  scene.add(skydome);
  setupEnvironment(renderer, scene);
  const { setShadowMapSize, setKeyLightDir } = setupLighting(scene);

  const grid = new THREE.GridHelper(GRID_SIZE, GRID_DIVISIONS, GRID_COLOR, GRID_COLOR);
  const gridMaterial = grid.material as THREE.LineBasicMaterial;
  gridMaterial.transparent = true;
  gridMaterial.opacity = GRID_OPACITY;
  grid.visible = false;
  scene.add(grid);

  const camera = new THREE.PerspectiveCamera(CAMERA_FOV_2D, 1, CAMERA_NEAR, CAMERA_FAR);
  const orthoCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, CAMERA_NEAR, CAMERA_FAR);

  const MOUSE_BUTTONS = { LEFT: null, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.ROTATE };

  const controls = new OrbitControls(camera, canvas);
  controls.enableDamping = true;
  controls.enabled = false;
  controls.mouseButtons = MOUSE_BUTTONS;
  controls.minDistance = CAMERA_MIN_DISTANCE;
  controls.maxDistance = CAMERA_MAX_DISTANCE;

  const orthoControls = new OrbitControls(orthoCamera, canvas);
  orthoControls.enableDamping = true;
  orthoControls.enableRotate = false;
  orthoControls.enabled = false;
  orthoControls.mouseButtons = MOUSE_BUTTONS;
  orthoControls.minZoom = ORTHO_MIN_ZOOM;

  const rig = new CameraRig(camera, orthoCamera, controls, orthoControls);

  const composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, rig.activeCamera);
  composer.addPass(renderPass);
  const bloomPass = new UnrealBloomPass(
    new THREE.Vector2(window.innerWidth, window.innerHeight),
    BLOOM_STRENGTH,
    BLOOM_RADIUS,
    BLOOM_THRESHOLD,
  );
  bloomPass.enabled = BLOOM_STRENGTH > 0;
  composer.addPass(bloomPass);
  const outputPass = new OutputPass();
  composer.addPass(outputPass);

  let phaseTimer: PhaseTimer | null = null;

  /**
   * Brackets a composer pass's GPU work with a named phase timer when one is
   * registered (bench mode). The passes run sequentially inside composer.render,
   * so wrapping each one keeps every TIME_ELAPSED span disjoint and non-nested.
   * A no-op when no timer is set, so normal rendering is untouched.
   */
  function timePass(pass: Pass, name: string): void {
    const orig: Pass["render"] = pass.render.bind(pass);
    pass.render = (renderer, writeBuffer, readBuffer, deltaTime, maskActive): void => {
      if (phaseTimer === null) {
        orig(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
        return;
      }
      phaseTimer.begin(name);
      orig(renderer, writeBuffer, readBuffer, deltaTime, maskActive);
      phaseTimer.end(name);
    };
  }
  timePass(renderPass, RENDER_PHASE_SCENE);
  timePass(bloomPass, RENDER_PHASE_BLOOM);
  timePass(outputPass, RENDER_PHASE_POST);

  function setPhaseTimer(timer: PhaseTimer | null): void {
    phaseTimer = timer;
  }

  let lastWidth = window.innerWidth;
  let lastHeight = window.innerHeight;
  let bloomBaseW = window.innerWidth;
  let bloomBaseH = window.innerHeight;
  let postFxBypassed = false;
  let currentBloomMode: BloomMode = initialBloomMode;
  let feedOpen = false;
  let feedRect: { x: number; y: number; w: number; h: number } | null = null;
  let feedW = 0;
  let feedH = 0;

  /**
   * Single point that governs the bloom pass's enabled state and internal
   * resolution. Quarter/Half shrink the mip/blur chain to a fraction of the
   * physical framebuffer (the final composite upscales it back), so only the
   * glow softens while the scene stays full-res. composer.setSize resets the
   * pass to full dims on every resize, so resize re-applies this afterwards.
   * Sizes use the renderer pixel ratio because composer passes run in physical
   * pixels.
   */
  function applyBloomMode(mode: BloomMode): void {
    currentBloomMode = mode;
    bloomPass.enabled = mode !== "off";
    const scale = mode === "quarter" ? 0.25 : 0.5;
    const pixelRatio = renderer.getPixelRatio();
    bloomPass.setSize(bloomBaseW * pixelRatio * scale, bloomBaseH * pixelRatio * scale);
  }

  function resize(width: number, height: number): void {
    lastWidth = width;
    lastHeight = height;
    renderer.setSize(width, height, false);
    if (feedRect !== null) {
      feedW = 0;
      feedH = 0;
      return;
    }
    bloomBaseW = width;
    bloomBaseH = height;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    rig.updateOrthoFrustum(width / height);
    composer.setSize(width, height);
    applyBloomMode(currentBloomMode);
  }

  /**
   * Enters / leaves Community feed rendering. While active, render() clears the
   * whole canvas every frame and paints a focused scene into its card rect, and
   * the canvas element is CSS-clipped to that rect (rounded top corners) so the
   * playing scene takes the card's shape — the rounded clip the rectangular
   * scissor cannot give. The clipped-away margins reveal the page background.
   * Leaving restores the full-screen editor render (scissor off, clip removed,
   * composer/camera resized to the window).
   */
  function setFeedActive(active: boolean): void {
    if (active === feedOpen) {
      return;
    }
    feedOpen = active;
    if (active) {
      renderer.domElement.style.clipPath = "inset(100%)";
    } else {
      renderer.domElement.style.clipPath = "";
      feedRect = null;
      feedW = 0;
      feedH = 0;
      renderer.setScissorTest(false);
      resize(lastWidth, lastHeight);
    }
  }

  /**
   * Sets (or clears) the focused card's rectangle within feed mode. The canvas is
   * clipped to the rect every frame (scrolling moves it), with rounded top corners
   * matching the card. The composer, bloom mip chain and camera aspect are sized
   * to the card's pixel box only on a size change, so the render targets are never
   * reallocated mid-scroll. Passing null leaves feed mode armed with no focus
   * (canvas fully clipped away). The canvas itself never moves or resizes.
   */
  function setFeedRect(rect: { x: number; y: number; w: number; h: number } | null): void {
    if (rect === null) {
      feedRect = null;
      renderer.domElement.style.clipPath = "inset(100%)";
      return;
    }
    feedRect = rect;
    const top = Math.max(0, rect.y);
    const left = Math.max(0, rect.x);
    const right = Math.max(0, lastWidth - (rect.x + rect.w));
    const bottom = Math.max(0, lastHeight - (rect.y + rect.h));
    renderer.domElement.style.clipPath = `inset(${top}px ${right}px ${bottom}px ${left}px round 12px 12px 0 0)`;
    const w = Math.max(1, Math.round(rect.w));
    const h = Math.max(1, Math.round(rect.h));
    if (w === feedW && h === feedH) {
      return;
    }
    feedW = w;
    feedH = h;
    bloomBaseW = w;
    bloomBaseH = h;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    rig.updateOrthoFrustum(w / h);
    composer.setSize(w, h);
    applyBloomMode(currentBloomMode);
  }

  function render(activeCam: THREE.Camera): void {
    activeCam.getWorldPosition(skydome.position);
    if (postFxBypassed) {
      renderer.render(scene, activeCam);
      return;
    }
    renderPass.camera = activeCam;
    if (feedOpen) {
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, lastWidth, lastHeight);
      renderer.setScissor(0, 0, lastWidth, lastHeight);
      renderer.clear();
      if (feedRect !== null) {
        const vy = lastHeight - (feedRect.y + feedRect.h);
        renderer.setViewport(feedRect.x, vy, feedRect.w, feedRect.h);
        renderer.setScissor(feedRect.x, vy, feedRect.w, feedRect.h);
        renderer.setScissorTest(true);
        composer.render();
      }
      return;
    }
    composer.render();
  }

  /**
   * Debug-only GPU cost switches (?debug Perf section) for isolating what makes
   * a weak GPU frame-bound. Bypassing post FX skips the OutputPass, so the
   * direct render path relies on the renderer's own tone mapping / colorspace.
   * The Bloom mode (Off/Quarter/Half) drives applyBloomMode, so A/B
   * measurement can compare reduced-resolution bloom against the skipped
   * variant. Pixel ratio is set before applyBloomMode so the bloom dims pick
   * up the new ratio.
   */
  function setDebugRender(state: DebugRenderState): void {
    const pixelRatio = state.dprCap1 ? 1 : Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(pixelRatio);
    composer.setPixelRatio(pixelRatio);
    applyBloomMode(state.bloom);
    postFxBypassed = !state.postFx;
    resize(lastWidth, lastHeight);
  }

  /**
   * Sets only the render pixel ratio (the DPR-cap subset of setDebugRender),
   * leaving bloom / postFx untouched. Used by bench mode's ?dpr1=1 so
   * forcing DPR=1 for measurement does not clobber a fixture's bloom state.
   */
  function setDprCap1(cap: boolean): void {
    const pixelRatio = cap ? 1 : Math.min(window.devicePixelRatio, 2);
    renderer.setPixelRatio(pixelRatio);
    composer.setPixelRatio(pixelRatio);
    resize(lastWidth, lastHeight);
  }

  resize(window.innerWidth, window.innerHeight);

  return { renderer, scene, rig, grid, resize, render, setFeedActive, setFeedRect, setDebugRender, setDprCap1, setBloomMode: applyBloomMode, setPhaseTimer, setShadowMapSize, setKeyLightDir };
}
